// ============================================================================
// SAMPLE SERVICE HOOKS (Reference Implementation)
// ============================================================================
// React hooks for Service Master API operations

import { useState, useEffect, useCallback } from 'react';
import { sampleServiceService } from 'api/services';
import type {
  SampleService,
  SampleServiceListItem,
  CreateSampleServiceRequest,
  UpdateSampleServiceRequest,
  SampleServiceSearchRequest,
  SampleServiceFilterParams,
  SimilarSampleServiceSuggestion,
  ID,
} from 'api/types';

/**
 * Hook to fetch all services (paginated)
 */
export const useSampleServices = (params?: SampleServiceSearchRequest) => {
  const [data, setData] = useState<SampleServiceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Serialize params to avoid infinite re-render when caller passes an object literal.
  // When caller uses useState for params (recommended), this is a safety net.
  const paramsKey = JSON.stringify(params);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await sampleServiceService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchServices,
  };
};

/**
 * Hook to fetch a single service by ID
 */
export const useSampleService = (id: ID) => {
  const [data, setData] = useState<SampleService | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchService = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await sampleServiceService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchService();
  }, [fetchService]);

  return {
    data,
    loading,
    error,
    refetch: fetchService,
  };
};

/**
 * Hook to create a new service
 */
export const useCreateSampleService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createSampleService = async (
    data: CreateSampleServiceRequest,
    options?: {
      onSuccess?: (service: SampleService) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await sampleServiceService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createSampleService,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update a service
 */
export const useUpdateSampleService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateSampleService = async (
    id: ID,
    data: UpdateSampleServiceRequest,
    options?: {
      onSuccess?: (service: SampleService) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await sampleServiceService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateSampleService,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete a service
 */
export const useDeleteSampleService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteSampleService = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await sampleServiceService.delete(id);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteSampleService,
    loading,
    error,
    success,
  };
};

/**
 * Hook to search services
 */
export const useSearchSampleServices = () => {
  const [data, setData] = useState<SampleServiceListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const searchSampleServices = async (params: SampleServiceSearchRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await sampleServiceService.search(params);
      setData(response.data);
      setPagination(response.pagination);
      return response;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    searchSampleServices,
    data,
    loading,
    error,
    pagination,
  };
};

/**
 * Hook to get similar service names
 */
export const useSimilarSampleServiceNames = () => {
  const [data, setData] = useState<SimilarSampleServiceSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const getSimilarNames = async (name: string) => {
    if (!name || name.length < 3) {
      setData([]);
      return [];
    }

    setLoading(true);
    setError(null);
    try {
      const response = await sampleServiceService.getSimilarNames(name);
      setData(response.data);
      return response.data;
    } catch (err) {
      setError(err as Error);
      setData([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  return {
    getSimilarNames,
    suggestions: data,
    loading,
    error,
  };
};

/**
 * Hook to get active services
 */
export const useActiveSampleServices = () => {
  const [data, setData] = useState<SampleServiceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchActiveServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await sampleServiceService.getActive();
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveServices();
  }, [fetchActiveServices]);

  return {
    data,
    loading,
    error,
    refetch: fetchActiveServices,
  };
};

/**
 * Hook to bulk delete services
 */
export const useBulkDeleteSampleServices = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const bulkDelete = async (
    ids: ID[],
    options?: {
      onSuccess?: (result: any) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await sampleServiceService.bulkDelete(ids);
      setSuccess(true);
      options?.onSuccess?.(response);
      return response;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    bulkDelete,
    loading,
    error,
    success,
  };
};
